import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Button from '@welldev-group/rcl-button';
import { I18nText } from '@welldev-group/react-comp-lib';
import { debounce } from 'lodash';
import TextEditor from '../TextEditor';
import routes from '../../../shared/routes';
import httpClient from '../../../shared/libraries/httpClient';

const MessageTyper = props => {
  const {
    selectedSupportTicketId,
    fetchTickets,
    fetchMessages,
    uncommittedMessage,
    uncommittedMessageId,
    signedInAsSuperAdmin,
    editing,
    setMessageToEdit,
    setMessageToEditId,
  } = props;

  const [message, setMessage] = useState('');
  const [attachmentIds, setAttachmentIds] = useState([]);
  const [inputError, setInputError] = useState(null);

  useEffect(() => {
    setMessage(uncommittedMessage);
  }, [uncommittedMessage]);

  const params = {
    support_ticket_message: {
      body: message,
      attachment_ids: attachmentIds,
    },
  };

  const saveUncommittedMessageWithDebounce = useRef(
    debounce(async (ticketId, text, id, attachmentIds) => {
      const paramsToPass = {
        support_ticket_message: {
          body: text,
          attachment_ids: attachmentIds,
        },
      };

      try {
        await httpClient.put(
          routes.support.message.update({
            support_ticket_id: ticketId,
            id,
          }),
          paramsToPass,
        );
      } catch (e) {
        const statusCode = e?.status || e?.response?.status;
        const errorMessage =
          statusCode === 422
            ? I18n.t('components.support.ticket.errors.invalid_data_error')
            : I18n.t('components.support.ticket.errors.generic_error');
        setInputError(errorMessage);
      }
    }, 1000),
  ).current;

  const saveNewMessage = async () => {
    saveUncommittedMessageWithDebounce.flush();

    const route = signedInAsSuperAdmin
      ? routes.support.message.saveDraft({
          support_ticket_id: selectedSupportTicketId,
          id: uncommittedMessageId,
        })
      : routes.support.message.send({
          support_ticket_id: selectedSupportTicketId,
          id: uncommittedMessageId,
        });

    const response = await httpClient.put(route, params);

    if (response.data.error === null) {
      fetchMessages().then(() => {
        setMessage('');
      });

      fetchTickets();
    }
  };

  const saveInternalMessage = async () => {
    const { data } = await httpClient.put(
      routes.support.message.saveInternal({
        support_ticket_id: selectedSupportTicketId,
        id: uncommittedMessageId,
      }),
      params,
    );

    if (data.error === null) {
      fetchMessages().then(() => {
        setMessage('');
      });

      fetchTickets();
    }
  };

  const updateMessage = async () => {
    const { data } = await httpClient.put(
      routes.support.message.update({
        support_ticket_id: selectedSupportTicketId,
        id: uncommittedMessageId,
      }),
      params,
    );

    if (data.error === null) {
      setMessageToEditId(null);
      setMessageToEdit(null);
      fetchMessages().then(() => {
        setMessage('');
      });
    }
  };

  const onEditorMessageChange = text => {
    setMessage(text);

    if (!editing) {
      saveUncommittedMessageWithDebounce(
        selectedSupportTicketId,
        text,
        uncommittedMessageId,
        attachmentIds,
      );
    }
  };

  const disableSubmitButton = () => {
    return !message?.replace(/<(?!img)[^>]+>|[\s]+/g, '');
  };

  const handleAttachmentId = id => {
    setAttachmentIds(ids => [...ids, id]);
  };

  return (
    <>
      <TextEditor
        value={message}
        onAttachmentUpload={handleAttachmentId}
        onChange={onEditorMessageChange}
        error={inputError}
      />
      <div className="support-message-thread__actions">
        {editing ? (
          <>
            <Button
              onClick={updateMessage}
              label={<I18nText id="support.save" />}
              version="v2"
              size="tiny"
              className="support-message-thread__button"
              disabled={disableSubmitButton()}
            />
            <Button
              onClick={() => {
                setMessageToEdit(null);
              }}
              label={<I18nText id="support.ticket.cancel" />}
              version="v2"
              size="tiny"
              type="primary"
              className="support-message-thread__button"
              disabled={disableSubmitButton()}
            />
          </>
        ) : (
          <>
            {signedInAsSuperAdmin && (
              <Button
                onClick={saveInternalMessage}
                label={<I18nText id="support.internal" />}
                version="v2"
                size="tiny"
                className="support-message-thread__button"
                disabled={disableSubmitButton()}
              />
            )}
            <Button
              onClick={saveNewMessage}
              label={<I18nText id="support.submit" />}
              version="v2"
              size="tiny"
              type="primary"
              className="support-message-thread__button"
              disabled={disableSubmitButton()}
            />
          </>
        )}
      </div>
    </>
  );
};

MessageTyper.propTypes = {
  selectedSupportTicketId: PropTypes.number.isRequired,
  fetchTickets: PropTypes.func.isRequired,
  fetchMessages: PropTypes.func.isRequired,
  uncommittedMessage: PropTypes.string.isRequired,
  uncommittedMessageId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  signedInAsSuperAdmin: PropTypes.bool.isRequired,
  editing: PropTypes.bool.isRequired,
  setMessageToEdit: PropTypes.func.isRequired,
  setMessageToEditId: PropTypes.func.isRequired,
};

export default MessageTyper;
